<template>
  <div class="tag-list-container">
    <div class="tag-list">
      <TagPill
        v-for="tag in tagsList.shown"
        :key="tag.id"
        :tag="tag"
        :editable="config.editable"
        :removeable="config.removeable"
        :color-label="config.useColorLabels"
        @remove-tag="emit('remove-tag', tag)"
        @click-tag="handleTagClick(tag)"
      />
    </div>
    <Tooltip
      v-if="tagsList.hidden.length > 0"
      placement="bottom-start"
      class-name="hidden-tags"
      :options="tippyOptions"
    >
      <template #trigger>
        <span>
          <i :class="hiddenTagClass" aria-label="Show All"></i>
          {{ tagsList.hidden.length }}
        </span>
      </template>
      <template #tooltip>
        <span class="tag-list tag-list-dt-column">
          <TagPill
            v-for="tag in tagsList.hidden"
            :key="tag.id"
            :tag="tag"
            :editable="config.editable"
            :removeable="config.removeable"
            :color-label="config.useColorLabels"
            @remove-tag="emit('remove-tag', tag)"
            @click-tag="handleTagClick(tag)"
          />
        </span>
      </template>
    </Tooltip>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, ref, watchEffect } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import TagPill from "@/Tags/TagPill.vue";
import { Tag } from "@/typings/tag";

const props = defineProps({
  tags: {
    type: Array as () => Tag[],
    required: true
  },
  config: {
    type: Object as () => {
      editable: boolean;
      removeable: boolean;
      truncList: boolean;
      truncCount: number;
      clickEmit: string;
      useColorLabels: boolean;
    },
    default: () => ({
      editable: false,
      removeable: false,
      truncList: false,
      truncCount: 1,
      clickEmit: "add-tag",
      useColorLabels: false
    })
  }
});

const emit = defineEmits<{
  (e: "remove-tag"): void;
  (e: string, value: string, tag: Tag): void;
}>();

const hiddenTagClass = computed(() => {
  const classList = ["fa-regular", "sera-ml-auto"];
  if (showTruncatedTags.value) {
    classList.push("fa-square-minus");
  } else {
    classList.push("fa-square-plus");
  }
  return classList.join(" ");
});

const tippyOptions = computed(() => {
  return {
    interactive: false,
    delay: [0, 0],
    arrow: false,
    distance: 2
  };
});

const showTruncatedTags = ref(false);

const tagsList = ref<{ shown: Tag[]; hidden: Tag[] }>({
  shown: [],
  hidden: []
});
watchEffect(() => {
  if (props.config.truncList && props.tags.length > props.config.truncCount) {
    tagsList.value.shown = props.tags.slice(0, props.config.truncCount);
    tagsList.value.hidden = props.tags.slice(props.config.truncCount);
  } else {
    tagsList.value.shown = [...props.tags];
    tagsList.value.hidden = [];
  }
});

const handleTagClick = (tag: Tag) => {
  if (props.config.clickEmit) {
    emit(props.config.clickEmit, tag);
  }
};
</script>

<style scoped>
.tag-list-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 5px;
}
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tag-list.tag-list-dt-column {
  flex-direction: column;
  gap: 5px;
}
</style>
